import { FormatColorFillOutlined, FormatColorText } from '@mui/icons-material';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import { Box, Divider, Tooltip } from '@mui/material';
import { useCurrentEditor, useEditorState } from '@tiptap/react';
import { intersection, map } from 'lodash';
import { useState } from 'react';

import { OptionsButtonGroup } from './OptionsButtonGroup';
import { OptionMenuItem, OptionsDropdown } from './OptionsDropdown';
import TooltipToggleButton from './TooltipToggleButton';

const POSSIBLE_FORMATS = ['bold', 'italic', 'underline'];
const POSSIBLE_HEADING_LEVELS = [1, 2, 3, 4, 5, 6] as const;

const MenuBar = () => {
  const { editor } = useCurrentEditor();
  const editorState = useEditorState({
    editor,
    selector: (ctx) => {
      const node = ctx.editor?.state.selection.$from.node();

      return {
        color: ctx.editor?.getAttributes('textStyle').color,
        backgroundColor: ctx.editor?.getAttributes('textStyle').backgroundColor,
        isBold: ctx.editor?.isActive('bold'),
        isItalic: ctx.editor?.isActive('italic'),
        isUnderline: ctx.editor?.isActive('underline'),
        isHeading: node?.type.name === 'heading',
        headingLevel: node?.attrs.level,
        isParagraph: node?.type.name === 'paragraph',
      };
    },
  });
  const [formats, setFormats] = useState<typeof POSSIBLE_FORMATS>([]);

  const handleFormat = (_: React.MouseEvent<HTMLElement>, newFormats: string[]) => {
    setFormats(newFormats);
  };

  return (
    <Box
      sx={{ display: 'flex', flexWrap: 'wrap', bgcolor: 'grey.200', borderTopLeftRadius: 5, borderTopRightRadius: 5 }}
    >
      <OptionsButtonGroup
        size="small"
        value={intersection(formats, POSSIBLE_FORMATS)}
        onChange={handleFormat}
        aria-label="text formatting"
      >
        <TooltipToggleButton
          title="Bold"
          value="bold"
          aria-label="bold"
          onClick={() => editor?.chain().focus().toggleBold().run()}
          selected={Boolean(editorState?.isBold)}
        >
          <FormatBoldIcon />
        </TooltipToggleButton>
        <TooltipToggleButton
          title="Italic"
          value="italic"
          aria-label="italic"
          onClick={() => editor?.chain().focus().toggleItalic().run()}
          selected={Boolean(editorState?.isItalic)}
        >
          <FormatItalicIcon />
        </TooltipToggleButton>
        <TooltipToggleButton
          title="Underline"
          value="underline"
          aria-label="underline"
          onClick={() => editor?.chain().focus().toggleUnderline().run()}
          selected={Boolean(editorState?.isUnderline)}
        >
          <FormatUnderlinedIcon />
        </TooltipToggleButton>
      </OptionsButtonGroup>
      <Divider flexItem orientation="vertical" sx={{ mx: 0.5, my: 1 }} />
      <OptionsButtonGroup
        size="small"
        value={intersection(formats, POSSIBLE_FORMATS)}
        onChange={handleFormat}
        aria-label="text colors"
      >
        <TooltipToggleButton
          title="Text Color"
          value="color-picker"
          aria-label="color-picker"
          sx={{ '&.Mui-selected': { color: editorState?.color } }}
          selected={Boolean(editorState?.color)}
          onClick={() => {
            if (!editorState?.color) {
              document.getElementById('tiptap-menubar-color-picker')?.click();
            } else {
              editor?.chain().focus().unsetColor().run();
            }
          }}
        >
          <FormatColorText />
        </TooltipToggleButton>
        <TooltipToggleButton
          title="Highight"
          value="background-picker"
          aria-label="background-picker"
          sx={{ '&.Mui-selected': { color: editorState?.backgroundColor } }}
          selected={Boolean(editorState?.backgroundColor)}
          onClick={() => {
            if (!editorState?.backgroundColor) {
              document.getElementById('tiptap-menubar-background-picker')?.click();
            } else {
              editor?.chain().focus().unsetBackgroundColor().run();
            }
          }}
        >
          <FormatColorFillOutlined />
        </TooltipToggleButton>
        <input
          id="tiptap-menubar-color-picker"
          type="color"
          onInput={(event) => editor?.chain().focus().setColor(event.currentTarget.value).run()}
          value={editorState?.color}
          data-testid="setColor"
          style={{ visibility: 'hidden', position: 'absolute', pointerEvents: 'none' }}
        />
        <input
          id="tiptap-menubar-background-picker"
          type="color"
          onInput={(event) => editor?.chain().focus().setBackgroundColor(event.currentTarget.value).run()}
          value={editorState?.backgroundColor}
          data-testid="setColor"
          style={{ visibility: 'hidden', position: 'absolute', pointerEvents: 'none' }}
        />
      </OptionsButtonGroup>
      <Divider flexItem orientation="vertical" sx={{ mx: 0.5, my: 1 }} />
      <Tooltip title="Text Size" placement="top">
        <OptionsDropdown
          value={editorState?.headingLevel ?? 0}
          onChange={(e) => {
            const level = e.target.value as (typeof POSSIBLE_HEADING_LEVELS)[number];

            if (POSSIBLE_HEADING_LEVELS.includes(level)) {
              editor?.chain().setHeading({ level }).run();
            } else {
              editor?.chain().setParagraph().run();
            }
          }}
        >
          <OptionMenuItem value={0} selected={editorState?.isParagraph}>
            Paragraph
          </OptionMenuItem>
          {map(POSSIBLE_HEADING_LEVELS, (level) => (
            <OptionMenuItem selected={editorState?.isHeading && editorState?.headingLevel === level} value={level}>
              Heading {level}
            </OptionMenuItem>
          ))}
        </OptionsDropdown>
      </Tooltip>
    </Box>
  );
};

export default MenuBar;
